/**
 * A query-report-header component defines the saving
 * and loading mechanisms for interacting with reports
 */
import {AnyAttrs, AttrsSchema, Component} from 'panel';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import {DashboardOrigin, getDashboardParams} from 'common/report/util/bookmark-overrides';
import {userDetailsStore} from 'common/report/stores/user-details';
import {featuresStore} from 'common/report/stores/features';
import {currentProjectStore} from 'common/report/stores/project-details';
import {EntityType, OverrideModal} from 'common/report/util/saved-entities/types';
import {Section, SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import {getBookmarkInDashboardHref, isBoardsUiEnabled} from 'common/report/util/boards';
import {SearchSortOption} from 'common/report/util/search/types';
import {ContentDiscoveryType, SearchSource, SearchTrackedProperty} from 'common/report/util/search/tracking';
import {
  buildCreateEntityModalAttrs,
  getEntityType,
  getEntityTypeName,
  getSharableEntityType,
  getShareCapability,
  promptSuperadminOverrideSave,
} from 'common/report/util/saved-entities';
import {parseStateFromUrl} from 'common/report/util/state-from-url';
import {emitTrackEvent, KEY_VALUES, propagateEvent} from 'common/util/dom';
import {serializeAttributes} from 'common/util';
import {pluralize} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {promptSharing} from 'common/widgets/sharing';
import {
  FeatureGate,
  Permission,
  ProjectFeatureFlag,
  RestrictedReport,
  RestrictEntitySaving,
} from 'common/report/constants';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {IconSizeType} from 'common/constants';
import {useStore} from 'common/util/component-helpers';
import {SharingAccessType} from 'common/widgets/sharing/types';
import {createEntityModalPromise} from 'common/widgets/create-entity-modal';
import {getSharingModalDisabledReason} from 'common/widgets/sharing/utils';
import {SharingModalDisabledReason, SharingModalViewType} from 'common/widgets/sharing/modal/types';
import {confirm} from 'common/components/confirm';
import {THEME} from 'common/components/confirm/types';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {IronContextRegistry} from 'common/report/context-registry';
import {DummyUrlManager} from 'common/contexts/url-manager/dummy';
import {addToast} from 'common/components/toast-area/toast-manager';
import {StatusCode} from 'common/util/shared-client';
import {ToastType} from 'common/components/toast/types';
import {upsellModalPromise} from 'common/widgets/upsell-modal';
import {Placement} from 'common/components/tooltip/types';
import {ButtonTheme} from 'common/components/button/constants';

import {loadEntityModalPromise} from '../load-entity-modal';
import {GlobalAccessType, ModalReturn} from '../create-entity-modal/types';
import {HeaderAction, QueryReportHeaderAttrs, queryReportHeaderAttrsSchema, SaveModes} from './types';
import {SaveTypeTrackingProperty, TrackingEvent} from './tracking';
import {template} from './template';
import css from './index.styl?inline';
import alertsUpsellImage from './images/alerts-upsell-images.svg';

const RESET_ACTION = Object.freeze({
  icon: `new-report`,
  value: HeaderAction.Reset,
  label: `New`,
  elref: `reportNewButton`,
});

const REFRESH_ACTION = Object.freeze({
  icon: `refresh`,
  value: HeaderAction.Refresh,
  label: `Refresh Data`,
  elref: `reportRefreshButton`,
});

interface QueryReportHeaderState {
  existingEntityNames: string[];
  isBookmarkSelectorOpen: boolean;
  isDescriptionTruncated: boolean;
  saveMode: SaveModes;
  openShareUponSave: boolean;
  showSaveBeforeShare: boolean;
  dashboardsLoading: boolean;
  isCreateAlertModalOpen: boolean;
  showPublicBadge: boolean;
  // boards
  canEditName: boolean;
  isEditingName: boolean;
  newReportName: string;
  wasNewReportSaved: boolean;
}
export class QueryReportHeader extends Component<
  QueryReportHeaderState,
  AnyAttrs,
  unknown,
  unknown,
  IronContextRegistry
> {
  private fetchedDashboard: DashboardOrigin;
  public dashboardController: DashboardSelectorController;
  private handlePublicDashboardUpdate = (evt: CustomEvent) => {
    const numActiveLinks = evt.detail[`numLinks`];
    // Once there is an update to public dashboard in sharing modal
    // we ignore the initial attribute
    this.removeAttribute(`is-public-dashboard`);
    this.update({showPublicBadge: numActiveLinks > 0});
  };

  static get attrsSchema(): AttrsSchema<QueryReportHeaderAttrs> {
    return {...super.attrsSchema, ...queryReportHeaderAttrsSchema};
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`urlManager` as const],
      defaultContexts: {
        urlManager: new DummyUrlManager(),
      },
      defaultState: {
        existingEntityNames: [],
        isBookmarkSelectorOpen: false,
        isDescriptionTruncated: true,
        saveMode: null,
        openShareUponSave: false,
        // Represents the state where we need to show Save modal before Share modal
        showSaveBeforeShare: false,
        dashboardsLoading: false,
        canEditName: false,
        isEditingName: false,
        isCreateAlertModalOpen: false,
        showPublicBadge: false,
        newReportName: null,
        wasNewReportSaved: false,
      },

      helpers: {
        IconSizeType,
        serializeAttributes,
        propagateEvent: (ev) => propagateEvent(this, ev),

        userIsEntityOwner: (entity): boolean => this.attr(`user-id`) === entity.userId,
        userIsStaff: (): boolean => userDetailsStore.value.isStaff,

        hasSelectedEntity: () => this.hasSelectedEntity,
        hasSlot: (name) => this.hasSlot(name),
        getHeaderActionOverflowItems: () => this.getHeaderActionOverflowItems(),
        getHeaderActionItems: () => this.getHeaderActionItems(),
        getHeaderActionItemsSlots: () => this.getHeaderActionItemsSlots(),
        getShareCapability: () => {
          // query-report-header gets entity object destructured through attrs
          const entity = {
            canShare: this.attr(`can-share`),
            canUpdateBasic: this.attr(`can-save`),
            allowStaffOverride: this.attr(`allow-staff-override`),
            isSuperadmin: this.attr(`is-superadmin`),
          };
          return getShareCapability(entity, EntityType.Report, {
            isSharingEnabled: this.helpers.isSharingEnabled(),
          });
        },
        allowSuperAdminOverride: () =>
          /** Allow super admin override for an entity that is valid but not save-able (due to permission) and user is a superadmin */
          (!this.attr(`can-save`) && !this.attr(`invalid-entity`) && this.attr(`is-superadmin`)) ||
          this.attr(`allow-staff-override`),
        handleSharingStatusButtonClick: async () => {
          // Open save modal when share is clicked in brand new report - only for share enabled projects
          if (this.helpers.shouldOpenSaveModalOnShare() && this.helpers.isSharingEnabled()) {
            this.update({
              openShareUponSave: true,
              showSaveBeforeShare: true,
            });
            this.openSaveModal(SaveModes.Save, SaveTypeTrackingProperty.Existing);
          } else {
            const sharingAccessType = this.helpers.getUserSharingAccessType();
            await promptSharing({
              type: this.helpers.getSharableEntityType(),
              id: this.attr(`selected-bookmark-id`),
              'project-id': this.attr(`project-id`),
              disabled: sharingAccessType === SharingAccessType.Viewer || sharingAccessType === SharingAccessType.None,
              'entity-name': this.attr(`selected-bookmark-name`),
              'disabled-reason': getSharingModalDisabledReason(sharingAccessType),
              'is-superadmin': this.helpers.allowSuperAdminOverride(),
              'entity-owner-id': this.attr(`selected-bookmark-user-id`),
            });
          }
        },
        handleViewPublicDashboardLinksClick: () => {
          const sharingAccessType = this.helpers.getUserSharingAccessType();
          promptSharing({
            type: this.helpers.getSharableEntityType(),
            id: this.attr(`selected-bookmark-id`),
            'project-id': this.attr(`project-id`),
            disabled: sharingAccessType === SharingAccessType.Viewer || sharingAccessType === SharingAccessType.None,
            'disabled-reason': getSharingModalDisabledReason(sharingAccessType),
            'open-as': SharingModalViewType.PublicDashboardDetails,
            'entity-name': this.attr(`selected-bookmark-name`),
          });
        },
        handleBoardButtonClick: () => this.track(TrackingEvent.ReturnToBoards, {Origin: `Back Button`}),
        handleBoardsDashboardClick: () => this.track(TrackingEvent.ReturnToBoards, {Origin: `Dashboard Title`}),
        handleBoardsSaveMenuChange: (ev) => {
          if (ev.detail.state === `open`) {
            this.track(TrackingEvent.StartSave);
          }
        },
        boardsSaveInDashboard: (dashboardId?: number, saveMode?: SaveModes) => {
          const dashboards = this.dashboardController.getDashboards();
          const {dashboard} = getDashboardParams(dashboards.find(({id}) => id === dashboardId) || this.getDashboard());
          if (dashboard) {
            this.fetchedDashboard = dashboard;
            if (saveMode) {
              this.update({saveMode});
            }
            this.submitSave({name: this.helpers.getReportName()}, null, dashboard);
          }
        },
        handleBoardsDuplicateMenuChange: (ev) => {
          if (ev.detail.state === `open`) {
            this.track(TrackingEvent.StartReportDuplicate, {'Duplicate Location': `Overflow`});
          }
        },
        handleAlertUpsellButtonClicked: () => {
          return upsellModalPromise(
            {
              header: `Real Time Alerts`,
              description: `Whether cart abandonment spiked or there's been a drop in user activation, Mixpanel can flag it for you. Control exactly when you're notified by selecting the reports to monitor and the thresholds that trigger alerts.`,
              'feature-gate-type': FeatureGate.AllowCustomAlerts,
              'learn-more-url': `/dashboards-alerts`,
              open: true,
              closeable: true,
              'upgrade-text': `Upgrade`,
            },
            {backgroundImage: alertsUpsellImage},
          );
        },
        handleCustomAlertCreated: () => {
          addToast({
            cta: `Manage Alerts`,
            ctaCallback: () => window.open(`/settings/account#alert-settings`, `_blank`),
            message: `Custom alert created.`,
          });
          this.helpers.closeCustomAlertModal();
        },
        handleCustomAlertError: (ev) => {
          addToast({
            message: `Failed to create custom alert${
              ev.detail.status === StatusCode.PermissionDenied ? `, Unauthorized` : ``
            }.`,
            type: ToastType.ERROR,
          });
          this.helpers.closeCustomAlertModal();
        },
        closeCustomAlertModal: () => {
          this.update({isCreateAlertModalOpen: false});
        },
        shouldOpenSaveModalOnShare: (): boolean => !this.attr(`selected-bookmark-id`) && !this.hasSelectedEntity,

        bookmarkSelected: (ev) => {
          const {value: report} = ev.detail.entity;
          this.dispatchEvent(new CustomEvent(`bookmarkselect`, {detail: {report}}));
        },

        // Set document title to report title
        updateDocumentTitle: (reportTitle = this.attr(`selected-bookmark-name`)) => {
          const projectName = this.project?.name;

          let newTitle;
          if (reportTitle && projectName) {
            newTitle = `${reportTitle} - ${projectName} / Mixpanel`;
          } else if (reportTitle) {
            newTitle = `${reportTitle} / Mixpanel`;
          } else if (projectName) {
            newTitle = `${projectName} / Mixpanel`;
          } else {
            newTitle = `Mixpanel`;
          }

          if (newTitle !== document.title) {
            document.title = newTitle;
          }
        },

        submitSaveModal: (result: ModalReturn, saveType?: SaveTypeTrackingProperty) => {
          if (result.event === `saveAndShare`) {
            // When Share Management is clicked in Save Modal, we need to open Share modal
            this.update({
              openShareUponSave: true,
            });
          }
          //save and share has a `.value` property, otherwise the report name and description is on the object
          this.submitSave(result.value ?? result, saveType);
        },

        /** Show save modal when editing is enabled */
        editReportClicked: async (ev: Event) => {
          // prevent the event from bubbling, without it a click on the edit button (pencil) would bubble
          // up to the title and cause a double modal invocation.
          ev.stopPropagation();
          if (this.helpers.allowSuperAdminOverride()) {
            const modalOutcome = await promptSuperadminOverrideSave(
              getEntityType(this.helpers.getSharableEntityType()),
            );
            if (modalOutcome === OverrideModal.Copy) {
              this.openSaveModal(SaveModes.SaveNew);
            } else if (modalOutcome !== OverrideModal.Closed) {
              this.openSaveModal(this.hasSelectedEntity ? SaveModes.Edit : SaveModes.Save);
            }
          } else {
            this.openSaveModal(this.hasSelectedEntity ? SaveModes.Edit : SaveModes.Save);
          }
        },

        getSamplingNotice: () => {
          const samplingFactor = this.attr(`sampling-factor`);
          if (samplingFactor && samplingFactor < 1) {
            return `Sampled at ${samplingFactor * 100}%`;
          }

          return null;
        },

        onHeaderActionClick: async (ev) => {
          switch (ev.detail.value) {
            case HeaderAction.Edit:
              if (this.isReportBoardsUiEnabled()) {
                this.update({isEditingName: true});
              } else {
                this.openSaveModal(SaveModes.Edit, SaveTypeTrackingProperty.Renamed);
              }
              this.track(TrackingEvent.StartReportRename, {'Rename Location': `Overflow`});
              break;
            case HeaderAction.Load:
              this.openBookmarkSelector();
              break;
            case HeaderAction.SaveNew:
              if (!this.hasSelectedEntity) {
                // if no bookmark is currently selected, show bookmark edit modal
                this.openSaveModal(SaveModes.Save);
              } else {
                this.openSaveModal(SaveModes.SaveNew);
              }
              break;
            case HeaderAction.Save:
              if (!this.hasSelectedEntity) {
                // if no bookmark is currently selected, show bookmark edit modal
                this.openSaveModal(SaveModes.Save);
              } else if (this.attr(`num-dashboards`) || this.attr(`num-custom-alerts`)) {
                // if current bookmark IS part of dashboards or alerts, warn user via dialog
                confirm({
                  theme: THEME.CONFIRM,
                  'confirm-button-label': `Save`,
                  title: `Save Report`,
                  description: this.helpers.getEditWarning(),
                }).then((confirmed) => {
                  if (confirmed && this.hasUnsavedChanges) {
                    this.submitSave();
                  }
                });
              } else if (this.helpers.allowSuperAdminOverride()) {
                const modalOutcome = await promptSuperadminOverrideSave(
                  getEntityType(this.helpers.getSharableEntityType()),
                );
                if (modalOutcome === OverrideModal.Copy) {
                  this.openSaveModal(SaveModes.SaveNew);
                } else if (modalOutcome !== OverrideModal.Closed && this.hasUnsavedChanges) {
                  this.submitSave(); // save directly
                }
              } else if (this.hasUnsavedChanges) {
                this.submitSave(); // save directly without any warning dialog
              }
              break;
            case HeaderAction.Reset:
              this.resetReport();
              break;
            case HeaderAction.CreateAlert:
              this.update({isCreateAlertModalOpen: true});
              break;
            case HeaderAction.ManageAlert:
              this.update({isCreateAlertModalOpen: false});
              window.open(`/settings/account/alert-settings/report/${this.attr(`selected-bookmark-id`)}`);
              break;
            case HeaderAction.EnableAlerts:
              this.helpers.handleAlertUpsellButtonClicked();
              break;
            case HeaderAction.QuerySampling:
              this.dispatchEvent(new CustomEvent(`actionclick`, {detail: ev.detail}));
              // TODO before deploy: can this toast be here? should it be repeated in every report?
              addToast({
                message: this.attr(`is-query-sampling-enabled`)
                  ? `Query sampling disabled successfully`
                  : `Query sampling enabled successfully`,
                type: ToastType.SUCCESS,
              });
              break;
            case HeaderAction.Delete:
              this.track(TrackingEvent.StartReportDelete);
              this.dispatchEvent(new CustomEvent(`actionclick`, {detail: ev.detail}));
              break;
            default:
              this.dispatchEvent(new CustomEvent(`actionclick`, {detail: ev.detail}));
              break;
          }
        },

        toggleDescriptionTruncation: () => {
          // This check allows the user to select text within the description
          // WITHOUT toggling truncation. Only toggle if there is no selection.
          if (!window.getSelection().toString()) {
            this.update({
              isDescriptionTruncated: !this.state.isDescriptionTruncated,
            });
          }
        },

        getEditWarning: () => {
          const numDashboards = this.attr(`num-dashboards`);
          const numDashboardsText = numDashboards
            ? `${numDashboards === 1 ? `a` : numDashboards} ${pluralize(`Dashboard`, numDashboards)}`
            : ``;
          const numAlerts = this.attr(`num-custom-alerts`);
          const numAlertsText = numAlerts
            ? `${numAlerts === 1 ? `an` : numAlerts} ${pluralize(`Alert`, numAlerts)}`
            : ``;
          const conjunction = numAlerts && numDashboards ? ` and ` : ``;

          return [
            `This report is currently active on`,
            ` ${numAlertsText}${conjunction}${numDashboardsText}.`,
            ` By continuing, you’ll be updating the data across all instances.`,
          ].join(``);
        },

        buildCreateEntityModalAttrs: () => {
          const isDuplicating = this.state.saveMode === SaveModes.SaveNew;
          const isEditing = this.hasSelectedEntity && !isDuplicating;
          const showEditWarning = !!(this.attr(`num-dashboards`) || this.attr(`num-custom-alerts`));

          return buildCreateEntityModalAttrs({
            modalTitleOverride: this.state.showSaveBeforeShare ? `Save Before Sharing` : null,
            modalOpen: true,
            isEditing,
            isDuplicating,
            projectId: this.attr(`project-id`),
            entityType: this.attr(`entity-type`),
            // showSaveBeforeShare represents if a Save modal was triggered by the Share button.
            // We show Share Management button in the Save modal only when Save modal was triggered by Save button
            showShareManagement: this.helpers.isSharingEnabled() && !this.state.showSaveBeforeShare,
            entity: {
              name: this.attr(`selected-bookmark-name`),
              description: this.attr(`selected-bookmark-description`),

              // permissions
              can_view: this.attr(`is-superadmin`) || this.attr(`can-save`),
              can_update_basic: this.attr(`can-save`),
              can_share: this.attr(`can-share`),
              is_superadmin: this.attr(`is-superadmin`),
              allow_staff_override: this.attr(`allow-staff-override`),
            },
            editWarning: showEditWarning && this.helpers.getEditWarning(),
            // Show copy link button on Save Before Share stage
            showCopyLinkButton: this.state.showSaveBeforeShare,
          });
        },
        canUserShare: () => this.helpers.getShareCapability().enableShare,
        getUserSharingAccessType: () => this.helpers.getShareCapability().sharingAccessType,
        isShareButtonEnabled: () => {
          const selectedBookmarkId = this.attr(`selected-bookmark-id`);
          // When a user loads a report and makes changes, the URL loses bookmark-id
          // in case a user either refreshes the page OR share the modified URL
          // we lose bookmark-id but app thinks we have selected a report
          // in this case we should disable share button as all share APIs will fail w/o bookmark-id
          if (this.hasSelectedEntity && !selectedBookmarkId) {
            return false;
          } else if (this.attr(`invalid-entity`)) {
            return false;
          } else if (!this.hasSelectedEntity && !selectedBookmarkId && !this.attr(`invalid-entity`)) {
            // Share button should be enabled for unsaved reports
            return true;
          } else if (!this.helpers.canUserShare()) {
            // Viewer/Consumer is not allowed to share, but should be able to access Viewer version of Share Modal
            return this.helpers.getUserSharingAccessType() !== SharingAccessType.Editor;
          } else {
            return true;
          }
        },

        getShareButtonTheme: () => {
          if (this.isReportBoardsUiEnabled() || this.isDashboardBoardsUiEnabled()) {
            return ButtonTheme.Tertiary;
          }
          return ButtonTheme.Primary;
        },

        showShareButton: () => {
          const {appName} = parseStateFromUrl(window.location.pathname) || {};
          const isExplore = this.attr(`entity-type`) === EntityType.Report && appName === `explore`;
          if (isExplore) {
            // TODO this logic is duplicated in mp-context, figure out how to consolidate
            return (
              featuresStore.value &&
              (featuresStore.value.hasAccessToReport(RestrictedReport.Cohorts) ||
                featuresStore.value.hasProjectFeatureFlag(ProjectFeatureFlag.Cohorts)) &&
              featuresStore.value.canSaveEntity(RestrictEntitySaving.Cohorts)
            );
          }
          return true;
        },

        isSharingEnabled: () => {
          return featuresStore.value?.getFeatureGate(FeatureGate.Sharing);
        },

        getSharableEntityType: () => getSharableEntityType(this.attr(`entity-type`)),

        getShareTooltipText: () => {
          const shareActionItem = this.getShareButtonAction();
          return shareActionItem.tooltip?.text ?? ``;
        },

        handleAutoRefresh: () =>
          this.dispatchEvent(new CustomEvent(`actionclick`, {detail: {value: HeaderAction.Refresh}})),
        getBackLink: () => {
          return getBookmarkInDashboardHref({
            dashboardId: this.getDashboard()?.id,
            bookmarkId: this.attr(`selected-bookmark-id`),
            projectId: this.attr(`project-id`),
            workspaceId: this.attr(`workspace-id`),
          });
        },
        isReportBoardsUiEnabled: () => this.isReportBoardsUiEnabled(),
        isDashboardBoardsUiEnabled: () => this.isDashboardBoardsUiEnabled(),
        isHeaderReady: () => !!currentProjectStore?.value,
        handleEditReportBoards: () => {
          this.track(TrackingEvent.StartReportRename, {'Rename Location': `Title`});
          this.update({isEditingName: true});
        },
        handleReportNameBlur: (ev) =>
          this.update({isEditingName: false, newReportName: ev.target.value, canEditName: false}),
        handleReportNameKeydown: (ev) => {
          if (ev.key === KEY_VALUES.ESCAPE) {
            ev.preventDefault();
            this.update({isEditingName: false});
          } else if (ev.key === KEY_VALUES.ENTER) {
            ev.preventDefault();
            this.update({isEditingName: false, newReportName: ev.target.value, canEditName: false});
          }
        },
        handleInputInsert: (vnode) => {
          requestAnimationFrame(() => {
            vnode.elm.focus();
          });
        },
        isAlertFeatureGateEnabled: () => {
          return featuresStore.value?.getFeatureGate(FeatureGate.AllowCustomAlerts);
        },
        isReportSaved: () => this.attr(`selected-bookmark-id`) && !this.hasUnsavedChanges,
        isAlertButtonDisabled: () => !this.helpers.isReportSaved() || Boolean(this.attr(`custom-alerts-disabled-text`)),
        getAlertsTooltipText: () => {
          if (!this.helpers.isReportSaved()) {
            return `To enable alerts, save your report`;
          }
          return this.attr(`custom-alerts-disabled-text`) || `Enable Alerts`;
        },
        getSaveAction: () => {
          const saveTooltipText = this.getSaveTooltipText();
          return {
            icon: `save`,
            label: `Save`,
            tooltip: saveTooltipText ? {text: saveTooltipText} : null,
            value: HeaderAction.Save,
            elref: `reportSaveButton`,
            isDisabled: this.isSaveButtonDisabled(),
          };
        },
        getReportName: () => {
          return this.state.newReportName === null
            ? this.attr(`selected-bookmark-name`) || `Untitled`
            : this.state.newReportName;
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    document.addEventListener(`PublicDashboardUpdated`, this.handlePublicDashboardUpdate);
    // update header when user details are updated (from backend or otherwise)
    const userStoreCallback = () => this.helpers.updateDocumentTitle();
    const setBoardsSuperPropertyCallback = () =>
      this.setSuperProperties({'Has Boards UI Test': this.isBoardsFlagEnabled()});
    useStore(this, userDetailsStore, userStoreCallback).fetch();
    useStore(this, featuresStore).fetch({projectId: this.attr(`project-id`)});
    useStore(this, currentProjectStore, setBoardsSuperPropertyCallback).fetch();

    if (this.canLoadDashboardData()) {
      this.loadDashboardData();
    }

    this.dashboardController = new DashboardSelectorController({
      workspaceId: this.attr(`workspace-id`),
      projectId: this.attr(`project-id`),
      dashboardsClient,
      bookmarkID: null,
    });
  }

  public disconnectedCallback() {
    // Reset document title as page navigates away
    this.helpers.updateDocumentTitle(``);
    document.removeEventListener(`PublicDashboardUpdated`, this.handlePublicDashboardUpdate);
    super.disconnectedCallback();
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `selected-bookmark-name`) {
      this.helpers.updateDocumentTitle();
    } else if (this.canLoadDashboardData()) {
      this.loadDashboardData();
    }

    const mpSharingModalElement = document.querySelector(`mp-sharing-modal`);
    if (attr === `is-save-failed` && this.attr(`is-save-failed`) && mpSharingModalElement) {
      mpSharingModalElement.setAttribute(`open`, false);
    }
    // In case where a user saves a new report or saves a report as a duplicate
    if (attr === `selected-bookmark-id` && (oldVal === null || oldVal !== newVal)) {
      const newBookmarkId = this.attr(`selected-bookmark-id`);
      if (newBookmarkId && this.state.openShareUponSave) {
        // no need to disable the sharing modal when a user is the creator except for consumer as creator
        const userIsConsumer = this.isUserConsumer();
        promptSharing({
          type: this.helpers.getSharableEntityType(),
          id: newBookmarkId,
          'project-id': this.attr(`project-id`),
          disabled: userIsConsumer,
          'disabled-reason': userIsConsumer ? SharingModalDisabledReason.Consumer : null,
        });
        this.update({
          openShareUponSave: false,
        });
      }
      this.update({
        wasNewReportSaved: this.initialized,
      });
    }
  }

  canLoadDashboardData() {
    return this.attr(`selected-bookmark-on-dashboard-id`) && !this.getDashboard() && this.isReportBoardsUiEnabled();
  }

  // Used in boards MVP only
  getDashboard() {
    return this.attr(`dashboard`) || this.fetchedDashboard;
  }

  get hasSelectedEntity() {
    return !!(this.attr(`selected-bookmark-id`) || this.attr(`selected-bookmark-name`));
  }

  get hasUnsavedChanges() {
    // if there is no entity selected/saved, then by definition there are unsaved changes
    return (
      !this.hasSelectedEntity ||
      this.attr(`has-unsaved-changes`) ||
      (this.isReportBoardsUiEnabled() && this.attr(`selected-bookmark-name`) !== this.helpers.getReportName())
    );
  }

  get project() {
    return userDetailsStore.value.projects[this.attr(`project-id`)] || null;
  }

  set existingEntityNames(existingEntityNames) {
    this.update({existingEntityNames});
  }

  /**
   * Checks for presence of a slotted child element
   */
  private hasSlot(slotName: string): boolean {
    return !!this.querySelector(`[slot="${slotName}"]`);
  }

  private openBookmarkSelector() {
    this.dispatchEvent(new CustomEvent(`bookmarkselectoropen`));

    loadEntityModalPromise({
      'entities-loading': this.attr(`entities-loading`),
      'display-context': this.attr(`display-context`),
      'modal-title': `Open Report`,
      'track-name': this.attr(`track-name`),
      'empty-message': `You don't have any saved reports yet.`,
      'project-id': this.attr(`project-id`),
      'user-id': this.attr(`user-id`),
      'workspace-id': this.attr(`workspace-id`),
    }).then((result) => {
      if (result.type === `close`) {
        this.dispatchEvent(new CustomEvent(`bookmarkselectorclose`));
      } else if (result.type === `select`) {
        this.helpers.bookmarkSelected(result);
      }
    });

    this.track(TrackingEvent.ReportListOpen, {
      [SearchTrackedProperty.ContentDiscoveryType]: ContentDiscoveryType.Browse,
      [SearchTrackedProperty.SortValue]: SearchSortOption.Default,
      [SearchTrackedProperty.Source]: SearchSource.Modal,
    });
  }

  private openSaveModal(saveMode: SaveModes, saveType?: SaveTypeTrackingProperty) {
    this.update({
      saveMode,
    });
    createEntityModalPromise(this.helpers.buildCreateEntityModalAttrs(), {
      existingEntityNames: this.state.existingEntityNames,
    }).then((result) => {
      if (result.didSave) {
        this.helpers.submitSaveModal(result, saveType);
      } else {
        this.closeSaveModal(result);
      }
    });
  }

  private closeSaveModal(result) {
    // When Cancel button is clicked inside the Save modal triggered by the Share button
    // we need to clear openShareUponSave state in order to prevent the Share modal
    // showing up in subsequent Save Modal unexpectedly
    const clearOpenShareUponSave = result.clearOpenShareUponSave;
    this.update({
      saveMode: null,
      showSaveBeforeShare: false,
      openShareUponSave: clearOpenShareUponSave ? false : this.state.openShareUponSave,
    });
  }

  private submitSave(
    newReportData: {
      didSave?: boolean;
      event?: HeaderAction;
      name?: string;
      description?: string;
      globalAccessType?: GlobalAccessType;
    } = null,
    saveType?,
    dashboard?,
  ) {
    const saveAsNew = this.state.saveMode === SaveModes.SaveNew || !this.hasSelectedEntity;
    if (!saveType) {
      saveType = saveAsNew ? SaveTypeTrackingProperty.New : SaveTypeTrackingProperty.Existing;
    }

    const reportData = {
      name: this.attr(`selected-bookmark-name`),
      description: this.attr(`selected-bookmark-description`),
      saveAsNew,
      dashboard: dashboard || this.getDashboard(),
      ...(newReportData || {}),
    };

    this.dispatchEvent(new CustomEvent(`save`, {detail: reportData}));

    this.update({
      saveMode: null,
      isEditingName: false,
      canEditName: false,
    });

    this.track(saveAsNew ? TrackingEvent.ReportSavedAsNew : TrackingEvent.ReportSaved, {
      'is owner': saveAsNew || this.attr(`user-id`) === this.attr(`selected-bookmark-user-id`),
      name: this.attr(`selected-bookmark-name`),
      description: this.attr(`selected-bookmark-description`),
      'Save Type': saveType,
    });

    // This opens a temporary Share Modal until save API finishes and returns. Once actual save completes via API,
    // another Share Modal will trigger (in attributeChangedCallback) w/the correct bookmark-id
    if (this.state.openShareUponSave) {
      promptSharing({
        type: this.helpers.getSharableEntityType(),
        id: this.attr(`selected-bookmark-id`),
        'project-id': this.attr(`project-id`),
        disabled: true,
      });
    }
  }

  private resetReport() {
    this.track(TrackingEvent.ResetState);
    this.dispatchEvent(new CustomEvent(`reset`));
  }

  private getHeaderActionItemsSlots() {
    const controlItems = this.getControlItems();
    return controlItems.filter((item) => Object.prototype.hasOwnProperty.call(item, `slot`));
  }

  private getControlItems() {
    let controlItems = this.attr(`control-items`);
    if (this.isReportBoardsUiEnabled()) {
      controlItems = controlItems.filter((item) => item.slot !== `dashboard-button`);
    }
    return controlItems;
  }

  private getHeaderActionOverflowItems(): Section[] {
    return [...this.getDefaultHeaderItems(), ...this.attr(`overflow-control-items`)];
  }

  private getDefaultHeaderItems(): Section[] {
    const canEdit = this.canEditReportName();
    const canDelete = this.hasSelectedEntity && this.attr(`can-write`);
    const entityTypeName = getEntityTypeName(this.attr(`entity-type`), {titleCased: true});

    const secondSection = {
      items: this.filterHiddenActions([
        ...(canEdit
          ? [
              {
                icon: `edit`,
                value: HeaderAction.Edit,
                label:
                  this.isReportBoardsUiEnabled() || this.isDashboardBoardsUiEnabled()
                    ? `Rename`
                    : `Edit ${entityTypeName}`,
                elref: `reportOverflowEditButton`,
              },
            ]
          : []),
        ...(canDelete
          ? [
              // TODO right now the deletion logic is on each report, when explore combines reports and cohorts without
              // having separate logic to delete separately we can move bookmark deletion logic back to this component
              // rather than separating it all out
              {
                icon: `trashcan`,
                value: HeaderAction.Delete,
                label:
                  this.isReportBoardsUiEnabled() || this.isDashboardBoardsUiEnabled()
                    ? `Delete`
                    : `Delete ${entityTypeName}`,
                elref: `reportDeleteButton`,
              },
            ]
          : []),
      ]),
    };
    const defaultSections: Section[] = [{items: this.filterHiddenActions([REFRESH_ACTION])}];

    if (canEdit || canDelete) {
      defaultSections.push(secondSection);
    }

    const extraSectionItems = [];

    if (this.attr(`is-custom-alerts-action-visible`)) {
      extraSectionItems.push(...this.getAlertItems());
    }

    if (this.attr(`is-query-sampling-action-visible`)) {
      extraSectionItems.push(...this.getQuerySamplingItems());
    }

    defaultSections.push({items: extraSectionItems});

    return defaultSections;
  }

  private isUserConsumer(): boolean {
    const userIsConsumer = !userDetailsStore.value.projects[this.attr(`project-id`)]?.permissions.includes(
      Permission.EditCreatedEntityVisibility,
    );
    return userIsConsumer && !userDetailsStore.value.isStaff;
  }

  isSaveButtonDisabled() {
    return !this.canSave() || !this.hasUnsavedChanges || !this.attr(`can-write`);
  }

  isBoardsSaveButtonDisabled() {
    return this.isSaveButtonDisabled() || this.state.dashboardsLoading;
  }

  isBoardsDuplicateButtonHidden() {
    return !this.attr(`selected-bookmark-id`) || this.state.wasNewReportSaved;
  }

  isDuplicateButtonDisabled() {
    return this.attr(`invalid-entity`) || !this.attr(`can-write`);
  }

  canSave() {
    return !this.attr(`invalid-entity`) && (this.attr(`can-save`) || this.helpers.allowSuperAdminOverride());
  }

  boardsCanSave() {
    return this.canSave() || (this.attr(`can-write`) && !this.attr(`selected-bookmark-id`));
  }

  canEditReportName() {
    if (this.isReportBoardsUiEnabled()) {
      return this.attr(`can-write`) || this.helpers.allowSuperAdminOverride();
    }
    return (
      this.hasSelectedEntity &&
      ((this.attr(`can-write`) && this.attr(`can-save`)) || this.helpers.allowSuperAdminOverride())
    );
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getHeaderActionItems(): Section {
    const saveTooltipText = this.getSaveTooltipText();
    const createdBy = {
      slot: `createdBy`,
      label: null,
      value: HeaderAction.CreatedBy,
    };

    const actions: SectionItem[] = [];

    if (this.isReportBoardsUiEnabled()) {
      // disabled states set in slot declaration
      actions.push(omit(this.getShareButtonAction(), `tooltip`));
    } else if (this.isDashboardBoardsUiEnabled()) {
      // When `is-select` mode is on for the share button, remove tooltip
      // section to avoid double tooltip for the share button and the
      // menu item in mp-select
      actions.push(
        omit(this.getShareButtonAction(), `tooltip`),
        createdBy,
        ...(this.attr(`can-write`) ? [RESET_ACTION] : []),
      );
    } else {
      // When `is-select` mode is on for the share button, remove tooltip
      // section to avoid double tooltip for the share button and the
      // menu item in mp-select
      actions.push(
        createdBy,
        omit(this.getShareButtonAction(), `tooltip`),
        ...(this.attr(`can-write`)
          ? [
              RESET_ACTION,
              this.helpers.getSaveAction(),
              {
                icon: `duplicate`,
                label: `Save as New`,
                tooltip: this.attr(`invalid-entity`) ? {text: saveTooltipText} : null,
                value: HeaderAction.SaveNew,
                elref: `reportSaveNewButton`,
                isDisabled: this.attr(`invalid-entity`),
              },
            ]
          : []),
        // In dashboards HeaderAction.Load and HeaderAction.Reset are hidden, and `alerts-button` is not given
        {
          value: HeaderAction.Load,
          label: `Open`,
          icon: `open`,
          elref: `reportLoadButton`,
        },
        {
          slot: `alerts-button`,
          label: ``,
        },
      );
    }

    return {
      items: [...this.filterHiddenActions(actions), ...this.getControlItems()],
    };
  }

  private getShareButtonAction() {
    let shareTooltip = null;
    if (!this.helpers.isShareButtonEnabled()) {
      if (this.attr(`invalid-entity`)) {
        shareTooltip = this.attr(`invalid-message`) || `Current report is invalid`;
      } else {
        const shareCapability = this.helpers.getShareCapability();
        shareTooltip = shareCapability.disabledShareReason;
        // this is a safeguard against buggy insights behavior. When a user loads a report, make changes, then refresh
        // or share the link, they will see `Cannot share due to consumer role` error message even though they are not
        // a consumer. This is due to insights not resetting data correctly on refresh. If there are any changes to a
        // loaded report - we can't not infer the entity from the URL. So upon refresh, insights lose all context
        // such as entity-id. But it keeps the entity name for some reason, making it look like we loaded a valid report
        // with changes. This if logic prevents the wrong message from showing up to prevent confusion. We detect this
        // odd state by seeing if id==0 and name='xyz' => I have selected bookmark name but no id!
        // TODO: make Insights app behave the same as the retention app
        if (
          shareTooltip &&
          !shareCapability.enableShare &&
          !this.attr(`selected-bookmark-id`) &&
          this.attr(`selected-bookmark-name`)
        ) {
          shareTooltip = `Save ${this.attr(`entity-type`)} to enable sharing`;
        }
      }
    }
    const shareAction = {
      slot: `sharing`,
      tooltip: shareTooltip ? {text: shareTooltip} : null,
      label: null,
      value: HeaderAction.Share,
    };

    return shareAction;
  }

  private track(event: string, props: Obj = {}) {
    if (this.attr(`track-name`)) {
      emitTrackEvent(this, {
        eventName: event,
        eventPrefix: `mp-query-report-header`,
        props: {
          ...props,
          project_id: this.attr(`project-id`),
          tab: this.attr(`track-name`),
          workspace_id: this.attr(`workspace-id`),
        },
        cancelable: true,
      });
    }
  }

  private setSuperProperties(properties: Obj = {}) {
    mixpanel.register(properties);
  }

  private filterHiddenActions(actions: Array<SectionItem<HeaderAction>>): Array<SectionItem<HeaderAction>> {
    const hiddenActions = new Set(this.attr(`hide-actions`));
    return actions.filter((action) => !hiddenActions.has(action.value));
  }

  private async loadDashboardData() {
    this.update({dashboardsLoading: true});
    this.fetchedDashboard = null;
    const dashboardIds = await dashboardsClient.queryBookmarkDashboardIds({
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
      bookmarkID: this.attr(`selected-bookmark-on-dashboard-id`),
    });

    // for some reason we get back both dashboardIds as well as dashboards with this call
    // possible for the dashboard attr to be populated before we need to sync, hence this extra call in case
    if (typeof dashboardIds[0] === `number` && dashboardIds.length > 0) {
      const dashboard = await dashboardsClient.querySingle({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
        dashboardID: dashboardIds[0],
      }).network;
      this.update({dashboardsLoading: false});
      this.fetchedDashboard = pick(dashboard, [`id`, `title`]);
    }
  }

  private isReportBoardsUiEnabled() {
    return (
      this.isBoardsFlagEnabled() && [`insights`, `funnels`, `retention`, `flows`].includes(this.attr(`display-context`))
    );
  }

  private isDashboardBoardsUiEnabled() {
    return this.isBoardsFlagEnabled() && this.attr(`display-context`) === `dashboard`;
  }

  private isBoardsFlagEnabled() {
    // Internal tests purposes:
    // ?force-ui-mode=boards: force new boards UI
    // ?force-ui-mode=none  : force common UI
    // other case           : we check the feature flag
    const forcedUiMode = this.isConnected && this.getContext(`urlManager`).getQueryStringParam(`force-ui-mode`);
    if (forcedUiMode === `boards`) {
      return true;
    }
    if (forcedUiMode === `none`) {
      return false;
    }

    return isBoardsUiEnabled(currentProjectStore.value);
  }

  getSaveTooltipText() {
    let saveTooltip = ``;
    if (this.isSaveButtonDisabled()) {
      const entityTypeName = getEntityTypeName(this.attr(`entity-type`));
      const canSave = this.canSave();
      if (this.attr(`invalid-entity`)) {
        saveTooltip = this.attr(`invalid-message`) || `Current ${entityTypeName} is invalid`;
      } else if (this.hasSelectedEntity && !canSave) {
        saveTooltip = this.helpers.isSharingEnabled()
          ? `You have view-only access and cannot save changes. Save as new to create a copy.`
          : `Editing is restricted on this report. Save as new to create a copy.`;
      } else if (!this.attr(`can-write`)) {
        saveTooltip = `You have view-only access and cannot save changes`;
      } else if (!this.hasUnsavedChanges) {
        saveTooltip = `No unsaved changes`;
      }
    }

    return saveTooltip;
  }

  private getQuerySamplingItems(): SectionItem[] {
    const disabledMessage = this.attr(`query-sampling-disabled-message`);
    const isQuerySamplingEnabled = this.attr(`is-query-sampling-enabled`);
    return [
      {
        label: isQuerySamplingEnabled ? `Disable query sampling` : `Enable query sampling`,
        icon: isQuerySamplingEnabled ? `filled-sampling` : `sampling`,
        highlightIcon: isQuerySamplingEnabled,
        isDisabled: disabledMessage,
        tooltip: disabledMessage ? {text: disabledMessage, placement: Placement.Left} : null,
        value: HeaderAction.QuerySampling,
      },
    ];
  }

  private getAlertItems() {
    if (this.helpers.isAlertFeatureGateEnabled()) {
      const tooltip: TooltipInfo = this.helpers.isAlertButtonDisabled()
        ? {text: this.helpers.getAlertsTooltipText(), placement: Placement.Left}
        : null;
      return [
        {
          label: `Alerts`,
          icon: `alert-normal`,
          isDisabled: this.helpers.isAlertButtonDisabled(),
          tooltip,
          subSections: [
            {
              items: [
                {
                  label: `Create Alert`,
                  value: HeaderAction.CreateAlert,
                  icon: `plus`,
                  isDisabled: this.helpers.isAlertButtonDisabled(),
                  tooltip,
                },
                {
                  label: `Manage Alerts`,
                  value: HeaderAction.ManageAlert,
                  icon: `settings`,
                  isDisabled: this.helpers.isAlertButtonDisabled(),
                  tooltip,
                },
              ],
            },
          ],
        },
      ];
    } else {
      const tooltip: TooltipInfo = {text: `Enable Alerts`, placement: Placement.Left};
      return [
        {
          label: `Alerts`,
          icon: `alert-normal`,
          tooltip,
          value: HeaderAction.EnableAlerts,
        },
      ];
    }
  }
}

export const QueryReportHeaderEl = defineAndMakeJsxFactory(`mp-query-report-header`, QueryReportHeader);
